import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DevicesIcon from "@mui/icons-material/Devices";
import HistoryIcon from "@mui/icons-material/History";
import CallIcon from "@mui/icons-material/Call";
import ChatIcon from "@mui/icons-material/Chat";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactsIcon from '@mui/icons-material/Contacts';
import FaceRecognitionIcon from '@mui/icons-material/FaceRetouchingNatural';
import EmailIcon from "@mui/icons-material/Email";
import SupportIcon from "@mui/icons-material/SupportAgent";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import RouterIcon from "@mui/icons-material/Router";
import PeopleIcon from "@mui/icons-material/People";
import GlassesIcon from "@mui/icons-material/Visibility";
import CampaignIcon from "@mui/icons-material/Campaign";
import MessagesIcon from "@mui/icons-material/Forum";
import DownloadIcon from "@mui/icons-material/Download"; // TEMPORARY -- AI interaction log export, added 2026-08-06
import "./sidebar.css";
import BASE_URL from "../../config";

const Sidebar = ({ isCollapsed = false }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/user-profile/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUsername(data.first_name || "User");
        setIsAdmin(data.is_admin);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        setIsLoading(false);
      });
  }, []);

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  if (isLoading) {
    return null;
  }

  const menuItems = isAdmin
    ? [
        {
          text: "User Management",
          path: "/user-dashboard/admin-dashboard",
          icon: <PeopleIcon />,
        },
        {
          text: "Device Management",
          path: "/user-dashboard/device-management",
          icon: <GlassesIcon />,
        },
        {
          text: "Case Management",
          path: "/user-dashboard/case-management",
          icon: <Inventory2Icon />,
        },
        {
          text: "Startup Message",
          path: "/user-dashboard/startup-message",
          icon: <CampaignIcon />,
        },
        {
          text: "OTA Service",
          path: "/user-dashboard/ota-service",
          icon: <DevicesIcon />,
        },
        {
          text: "Helpdesk",
          path: "/user-dashboard/helpdesk",
          icon: <SupportIcon />,
        },
        // TEMPORARY -- AI interaction log export, added 2026-08-06. See
        // REMOVE_ai_interaction_export.md at the repo root. Remove this
        // entry when the tool is retired.
        {
          text: "AI Interaction Export",
          path: "/user-dashboard/ai-export",
          icon: <DownloadIcon />,
        },
      ]
    : [
        {
          text: "Dashboard",
          path: "/user-dashboard/dashboard",
          icon: <DashboardIcon />,
        },
        {
          text: "My Devices",
          path: "/user-dashboard/my-devices",
          icon: <DevicesIcon />,
        },
        {
          text: "Location",
          path: "/user-dashboard/milestones",
          icon: <LocationOnIcon />,
        },
        {
          text: "My Contacts",
          path: "/user-dashboard/my-contacts",
          icon: <ContactsIcon />,
        },
        {
          text: "Call",
          path: "/user-dashboard/call",
          icon: <CallIcon />,
        },
        {
          text: "Face Recognition",
          path: "/user-dashboard/face-recognition",
          icon: <FaceRecognitionIcon />,
        },
        {
          text: "Device Chat",
          path: "/user-dashboard/chat",
          icon: <ChatIcon />,
        },
        {
          text: "Messages",
          path: "/user-dashboard/messages",
          icon: <MessagesIcon />,
        },
        {
          text: "My Subscription",
          path: "/user-dashboard/my-subscription",
          icon: <WorkspacePremiumIcon />,
        },
      ];



  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        className="mobile-sidebar-toggle"
        onClick={toggleMobileSidebar}
        aria-label="Open navigation menu"
        aria-expanded={isMobileOpen}
        aria-controls="sidebar-nav"
      >
        <MenuIcon aria-hidden="true" />
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar */}
      <aside
        id="sidebar-nav"
        className={`aws-sidebar ${isMobileOpen ? 'mobile-open' : ''} ${isCollapsed ? 'desktop-collapsed' : ''}`}
        aria-label="Main navigation"
      >
        {/* Header Section */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon" aria-hidden="true">
              <DashboardIcon sx={{ fontSize: 28 }} />
            </div>
            <div className="logo-text">
              <h2>Mavis Tech</h2>
              <span className="logo-subtitle">Management Console</span>
            </div>
          </div>

          {/* Mobile Close Button */}
          <button
            className="mobile-close-btn"
            onClick={closeMobileSidebar}
            aria-label="Close navigation menu"
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>

        {/* User Info */}
        <div className="sidebar-user-info" role="status" aria-label={`Logged in as ${username}, role: ${isAdmin ? "Administrator" : "User"}`}>
          <div className="user-avatar" aria-hidden="true">
            {username.charAt(0).toUpperCase()}
          </div>
          <div className="user-details">
            <h3 className="user-name">Hello, {username}</h3>
            <span className={`user-role ${isAdmin ? 'role-admin' : 'role-user'}`}>
              {isAdmin ? "Administrator" : "User"}
            </span>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav" aria-label="Main menu">
          <div className="nav-section-title" id="nav-section-label">NAVIGATION</div>
          <ul className="nav-menu" aria-labelledby="nav-section-label">
            {menuItems.map((item, index) => (
              <li key={index} className="nav-item">
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link"
                    onClick={closeMobileSidebar}
                  >
                    <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                    <span className="nav-text">{item.text}</span>
                    <span className="external-indicator" aria-hidden="true">↗</span>
                    <span className="sr-only">(opens in new tab)</span>
                  </a>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      isActive ? "nav-link active" : "nav-link"
                    }
                    onClick={closeMobileSidebar}
                  >
                    <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                    <span className="nav-text">{item.text}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="footer-info">
            <span className="footer-label">Version</span>
            <span className="footer-value">2.0.1</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;